import React from 'react';
import { useTranslation } from 'react-i18next';
import { Zap, Lock, CheckCircle2, Rocket, Shield, DollarSign, Target, Star, Gem } from 'lucide-react';

const WhyChooseUs = () => {
  const { t } = useTranslation();

  const reasons = [
    {
      id: 1,
      icon: <Zap className="w-6 h-6" />,
      emoji: '⚡',
      titleKey: 'whyChooseUs.reasons.instantActivation.title',
      features: [
        'whyChooseUs.reasons.instantActivation.feature1',
        'whyChooseUs.reasons.instantActivation.feature2',
        'whyChooseUs.reasons.instantActivation.feature3'
      ],
      gradient: 'from-yellow-400 to-orange-500',
      borderColor: 'border-yellow-200',
      bgColor: 'bg-yellow-50'
    },
    {
      id: 2,
      icon: <Lock className="w-6 h-6" />,
      emoji: '🔒',
      titleKey: 'whyChooseUs.reasons.enhancedPrivacy.title',
      features: [
        'whyChooseUs.reasons.enhancedPrivacy.feature1',
        'whyChooseUs.reasons.enhancedPrivacy.feature2',
        'whyChooseUs.reasons.enhancedPrivacy.feature3'
      ],
      gradient: 'from-blue-400 to-indigo-500',
      borderColor: 'border-blue-200',
      bgColor: 'bg-blue-50'
    },
    {
      id: 3,
      icon: <CheckCircle2 className="w-6 h-6" />,
      emoji: '✅',
      titleKey: 'whyChooseUs.reasons.authenticExperience.title',
      features: [
        'whyChooseUs.reasons.authenticExperience.feature1',
        'whyChooseUs.reasons.authenticExperience.feature2',
        'whyChooseUs.reasons.authenticExperience.feature3'
      ],
      gradient: 'from-green-400 to-emerald-500',
      borderColor: 'border-green-200',
      bgColor: 'bg-green-50'
    },
    {
      id: 4,
      icon: <Rocket className="w-6 h-6" />,
      emoji: '🚀',
      titleKey: 'whyChooseUs.reasons.lightningFastDelivery.title',
      features: [
        'whyChooseUs.reasons.lightningFastDelivery.feature1',
        'whyChooseUs.reasons.lightningFastDelivery.feature2',
        'whyChooseUs.reasons.lightningFastDelivery.feature3'
      ],
      gradient: 'from-purple-400 to-violet-500',
      borderColor: 'border-purple-200',
      bgColor: 'bg-purple-50'
    },
    {
      id: 5,
      icon: <Shield className="w-6 h-6" />,
      emoji: '🛡️',
      titleKey: 'whyChooseUs.reasons.reliableService.title',
      features: [
        'whyChooseUs.reasons.reliableService.feature1',
        'whyChooseUs.reasons.reliableService.feature2',
        'whyChooseUs.reasons.reliableService.feature3'
      ],
      gradient: 'from-gray-600 to-gray-800',
      borderColor: 'border-gray-200',
      bgColor: 'bg-gray-50'
    },
    {
      id: 6,
      icon: <DollarSign className="w-6 h-6" />,
      emoji: '💰',
      titleKey: 'whyChooseUs.reasons.exceptionalValue.title',
      features: [
        'whyChooseUs.reasons.exceptionalValue.feature1',
        'whyChooseUs.reasons.exceptionalValue.feature2',
        'whyChooseUs.reasons.exceptionalValue.feature3'
      ],
      gradient: 'from-amber-400 to-orange-500',
      borderColor: 'border-amber-200',
      bgColor: 'bg-amber-50'
    },
    {
      id: 7,
      icon: <Target className="w-6 h-6" />,
      emoji: '🎯',
      titleKey: 'whyChooseUs.reasons.serviceGuarantee.title',
      features: [
        'whyChooseUs.reasons.serviceGuarantee.feature1',
        'whyChooseUs.reasons.serviceGuarantee.feature2',
        'whyChooseUs.reasons.serviceGuarantee.feature3'
      ],
      gradient: 'from-red-400 to-pink-500',
      borderColor: 'border-red-200',
      bgColor: 'bg-red-50'
    },
    {
      id: 8,
      icon: <Star className="w-6 h-6" />,
      emoji: '🌟',
      titleKey: 'whyChooseUs.reasons.premiumFeatures.title',
      features: [
        'whyChooseUs.reasons.premiumFeatures.feature1',
        'whyChooseUs.reasons.premiumFeatures.feature2',
        'whyChooseUs.reasons.premiumFeatures.feature3'
      ],
      gradient: 'from-pink-400 to-purple-500',
      borderColor: 'border-pink-200',
      bgColor: 'bg-pink-50'
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-400 rounded-full px-6 py-3 text-sm font-semibold mb-6 backdrop-blur-sm">
            <Gem className="w-4 h-4 mr-2" />
            {t('whyChooseUs.badge')}
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('whyChooseUs.title')}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('whyChooseUs.subtitle')}
          </p>
        </div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((reason) => (
            <div
              key={reason.id}
              className={`group relative ${reason.bgColor} rounded-2xl border-2 ${reason.borderColor} p-6 hover:scale-[1.02] transition-all duration-300 hover:shadow-2xl`}
            >
              {/* Glow effect on hover */}
              <div className={`absolute inset-0 bg-gradient-to-r ${reason.gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`}></div>
              
              {/* Icon and Title */}
              <div className="relative z-10">
                <div className={`w-12 h-12 bg-gradient-to-r ${reason.gradient} rounded-xl flex items-center justify-center text-white shadow-lg mb-4`}>
                  {reason.icon}
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span>{reason.emoji}</span>
                  {t(reason.titleKey)}
                </h3>

                {/* Features */}
                <ul className="space-y-2">
                  {reason.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className={`text-transparent bg-gradient-to-r ${reason.gradient} bg-clip-text font-bold`}>•</span>
                      <span>{t(feature)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Decorative corner accent */}
              <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-br ${reason.gradient} opacity-10 rounded-bl-full rounded-tr-2xl`}></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-2xl font-bold text-white mb-6">
            {t('whyChooseUs.cta.title')}
          </p>
          <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:scale-105 hover:shadow-3xl transition-all duration-300">
            {t('whyChooseUs.cta.button')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;